import 'configs/initialize/googletag';
import ScreenSize from 'lib/ScreenSize';
import DeviceScreen from 'lib/DeviceScreen';
import { pickBy } from 'lodash';

export type Dimension = string | number[];

// Targeting for Hearst ads
export type Targeting = Partial<{
  slot: string;
  pub: string;
  mak: string | null;
  mod: string | null;
  fuel: string | null;
  type: string | null;
  yr: number | null;
  bpov: string;
  style: string | null;
  ntve: string;
  test: string;
  pubtemplate: string;
  prod: string;
  subprod: string;
  adflow: string;
  browserlanguage: string;
}>;

export const CCPA_OPT_OUT_LOCALSTORAGE_KEY = 'ccpaOptOut';

// enable the publisher ads service
export function enableService(): void {
  googletag.cmd.push(() => {
    googletag.pubads().enableSingleRequest();
    googletag.pubads().enableAsyncRendering();
    googletag.enableServices();
  });
}

// display the ads
// NOTE: ads may not always be available

export function clearTargeting(): void {
  googletag.cmd.push(() => {
    googletag.pubads().clearTargeting();
  });
}

export function setPageTargeting(targeting: Targeting): void {
  googletag.cmd.push(() => {
    googletag.pubads().setTagForChildDirectedTreatment(0);
  });

  Object.entries(pickBy(targeting)).forEach(([k, v]) => {
    const stringValue = v ? v.toString() : '';

    googletag.cmd.push(() => {
      googletag.pubads().setTargeting(k, stringValue);
    });
  });
}

export function restrictDataProcessing(): boolean {
  if (
    typeof window === 'undefined' ||
    window.localStorage.getItem(CCPA_OPT_OUT_LOCALSTORAGE_KEY) !== 'true'
  ) {
    googletag.cmd.push(() => {
      googletag.pubads().setPrivacySettings({
        restrictDataProcessing: false,
      });
    });

    return false;
  }

  googletag.cmd.push(() => {
    googletag.pubads().setPrivacySettings({
      restrictDataProcessing: true,
    });
  });

  return true;
}

export async function defineSlot<T>({
  adUnitPath,
  dimensions,
  elementId,
  targeting,
}: {
  adUnitPath: string;
  dimensions: Dimension[];
  elementId: string;
  targeting: T;
}): Promise<googletag.Slot> {
  return new Promise((resolve, reject) => {
    googletag.cmd.push(() => {
      // create google slot object
      const slot = googletag.defineSlot(adUnitPath, dimensions, elementId);

      if (!slot) {
        reject(
          new Error(
            `Could not create slot with adUnit: ${adUnitPath}, dimensions: ${dimensions}, element id: ${elementId}`
          )
        );
      }

      // add publisher ads service to the slot
      slot.addService(googletag.pubads());

      // set targeting for this slot, for autolist targeting params are
      // defined in JumpstartMapping table (e.g. make, model, year, etc)
      Object.entries(targeting).forEach(([k, v]) => {
        if (!v) {
          return;
        }

        slot.setTargeting(k, String(v));
      });

      resolve(slot);
    });
  });
}

export function display(elementId: string): void {
  googletag.cmd.push(() => {
    googletag.display(elementId);
  });
}

export function addEventListener(
  eventName: string,
  handler: (event: googletag.events.Event) => void
): void {
  googletag.cmd.push(() => {
    googletag.pubads().addEventListener(eventName, handler);
  });
}

export function destroySlot(elementId: string): void {
  googletag.cmd.push(() => {
    const slot = googletag
      .pubads()
      .getSlots()
      .find((currentSlot) => {
        return currentSlot.getSlotElementId() === elementId;
      });

    if (!slot) {
      return;
    }

    googletag.destroySlots([slot]);
  });
}

export function isInDeviceScreen(screenSizes: ScreenSize[]): boolean {
  return (
    !!screenSizes.length &&
    screenSizes.some((screenSize) => {
      return screenSize.includes(DeviceScreen.getWidth());
    })
  );
}

export function isBlankPixel(elementId: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const iframe: HTMLIFrameElement | null = window.document.querySelector(`#${elementId} iframe`);

  // If the iframe's contents can be accessed and contain a blank pixel container
  // then return true.  However if the source of the iframe has a different origin
  // then contentDocument will be null:
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLIFrameElement/contentDocument
  if (iframe && iframe.contentDocument) {
    return !!iframe.contentDocument.querySelector('.jcpblankpixel');
  }

  // otherwise we can't determine if the ad is a blank pixel or not
  return false;
}
